import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { PasswordInput, Button, Alert, Stack, Box } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { authApi } from "@/api/client";
import { useRouter } from "@tanstack/react-router";
import { useAuth } from "@/context/AuthContext";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export function ChangePasswordForm() {
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const { refreshUser } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setError(null);
    try {
      await authApi.changePassword(data.currentPassword, data.newPassword);
      await refreshUser();
      router.navigate({ to: "/" });
    } catch {
      setError("Failed to change password. Please check your current password.");
    }
  };

  return (
    <Box maw={400} mx="auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}
          <PasswordInput
            label="Current Password"
            {...register("currentPassword")}
            error={errors.currentPassword?.message}
            autoFocus
          />
          <PasswordInput
            label="New Password"
            {...register("newPassword")}
            error={errors.newPassword?.message}
          />
          <PasswordInput
            label="Confirm New Password"
            {...register("confirmPassword")}
            error={errors.confirmPassword?.message}
          />
          <Button type="submit" loading={isSubmitting} fullWidth>
            Change Password
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
